import type {
  BaseItem,
  Content,
  AlignmentPoints,
  SnapState,
} from '@messenger/types'
import type { MutableRefObject } from 'react'
import { calculateAlignmentSnap } from './calculateAlignmentSnap'

type Padding = Content['product']['padding']

type SizedItem = Pick<BaseItem, 'width' | 'height'>

type DragElement = {
  x: number
  y: number
  deltaX: number
  deltaY: number
}

type SnapStateRef = MutableRefObject<{
  vertical: SnapState
  horizontal: SnapState
}>

/** How long the first snap onto a guide holds the item there before the catch-up. */
export const SNAP_FREEZE_DURATION_MS = 400
/** How long the catch-up after the freeze takes to carry the item to the drag's position. */
export const SNAP_CATCH_UP_DURATION_MS = 200

type ComputeSnapOnEntryParams = {
  guidePosition: number
  itemSize: number
  padding: number
  unconstrainedPos: number
  snapStateRef: SnapStateRef | null
  axis: 'vertical' | 'horizontal'
}

/**
 * Computes snap-on-entry position for a single axis (horizontal or vertical).
 * Simple behavior: snap to guide center on first entry, freeze for 0.4s, then animate to cursor.
 */
const computeSnapOnEntry = ({
  guidePosition,
  itemSize,
  padding,
  unconstrainedPos,
  snapStateRef,
  axis,
}: ComputeSnapOnEntryParams): number => {
  // Calculate target snap position (center item on guide)
  // guidePosition is in canvas coords (includes padding)
  // We need target position in CSS coords (from padding edge)
  const targetPosition = guidePosition - itemSize / 2 - padding

  if (!snapStateRef?.current) {
    // No snap state tracking - just return unconstrained position
    return unconstrainedPos
  }

  const snapState = snapStateRef.current[axis]

  // Check if we're tracking this guide (with small tolerance for floating point)
  const isTrackingSameGuide =
    snapState.guidePosition !== null &&
    Math.abs(snapState.guidePosition - guidePosition) < 0.1

  if (!isTrackingSameGuide) {
    // First time entering this guide's zone - snap to center and record timestamp!
    snapState.guidePosition = guidePosition
    snapState.hasSnapped = true
    snapState.snapTimestamp = Date.now()
    return targetPosition
  }

  // We've already snapped to this guide - allow free movement
  return unconstrainedPos
}

/**
 * Applies snap-to-guide constraints to drag positions.
 * Smart "snap on entry" behavior with freeze and smooth animation:
 * - When item FIRST enters a guide's snap zone, it snaps to center
 * - Item is frozen for 0.4 seconds after initial snap (prevents immediate movement)
 * - After freeze period, item smoothly animates to cursor position (200ms)
 * - Then allows free movement (no stickiness)
 * - On subsequent drags (after releasing), no freeze - immediate free movement
 * - Only resets when item exits the snap zone completely
 * - Exiting and re-entering the zone will snap again with freeze
 */
export const applyDragConstraints = (
  dragElement: DragElement,
  item: SizedItem,
  padding: Padding,
  alignmentPoints: AlignmentPoints,
  snapToggle: boolean,
  snapStateRef: SnapStateRef | null = null,
) => {
  // Use dragElement.x/y as the UNCONSTRAINED position (where mouse wants to go)
  const unconstrainedX = dragElement.x
  const unconstrainedY = dragElement.y

  // Get padding for coordinate conversions
  const paddingLeft = padding?.left || 0
  const paddingTop = padding?.top || 0

  // Calculate item center positions for snap detection
  // Guide positions are stored in "canvas coordinates" (include padding)
  // dragElement.x/y from react-draggable are CSS positions (from padding edge)
  // To compare with guides, convert drag position to canvas coords by adding padding
  const itemCenterX = paddingLeft + unconstrainedX + item.width / 2
  const itemCenterY = paddingTop + unconstrainedY + item.height / 2

  // Get snap zone information from guides
  const { verticalSnapZone, horizontalSnapZone, snapInfo } =
    calculateAlignmentSnap(itemCenterX, itemCenterY, alignmentPoints)

  // Start with unconstrained position
  let finalX = unconstrainedX
  let finalY = unconstrainedY

  // Apply vertical (horizontal position) snap constraint
  if (snapToggle && verticalSnapZone) {
    const { guidePosition } = verticalSnapZone

    finalX = computeSnapOnEntry({
      guidePosition,
      itemSize: item.width,
      padding: paddingLeft,
      unconstrainedPos: unconstrainedX,
      snapStateRef,
      axis: 'vertical',
    })
  } else if (snapStateRef?.current) {
    // Not in any snap zone - reset vertical snap state so next entry is fresh
    snapStateRef.current.vertical = {
      guidePosition: null,
      hasSnapped: false,
      snapTimestamp: 0,
      hasCompletedSnap: false,
    }
  }

  // Apply horizontal (vertical position) snap constraint
  if (snapToggle && horizontalSnapZone) {
    const { guidePosition } = horizontalSnapZone

    finalY = computeSnapOnEntry({
      guidePosition,
      itemSize: item.height,
      padding: paddingTop,
      unconstrainedPos: unconstrainedY,
      snapStateRef,
      axis: 'horizontal',
    })
  } else if (snapStateRef?.current) {
    // Not in any snap zone - reset horizontal snap state so next entry is fresh
    snapStateRef.current.horizontal = {
      guidePosition: null,
      hasSnapped: false,
      snapTimestamp: 0,
      hasCompletedSnap: false,
    }
  }

  return { finalX, finalY, snapInfo }
}

/**
 * Applies snap-to-guide on drag release.
 * If item is within snap zone when released, snaps perfectly to guide center.
 */
export const applyReleaseSnap = (
  currentX: number,
  currentY: number,
  item: SizedItem,
  padding: Padding,
  alignmentPoints: AlignmentPoints,
  snapToggle: boolean,
) => {
  // If snap is disabled, return current position
  if (!snapToggle) {
    return { finalX: currentX, finalY: currentY }
  }

  // Get padding for coordinate conversions
  const paddingLeft = padding?.left || 0
  const paddingTop = padding?.top || 0

  // Calculate item center position in canvas coordinates
  const itemCenterX = paddingLeft + currentX + item.width / 2
  const itemCenterY = paddingTop + currentY + item.height / 2

  // Check if item is within any snap zones
  const { verticalSnapZone, horizontalSnapZone } = calculateAlignmentSnap(
    itemCenterX,
    itemCenterY,
    alignmentPoints,
  )

  // Start with current position
  let finalX = currentX
  let finalY = currentY

  // Snap horizontally if within vertical guide's snap zone
  if (verticalSnapZone) {
    const { guidePosition } = verticalSnapZone
    // Calculate perfect snap position (guide center minus half item width, in CSS coords)
    finalX = guidePosition - item.width / 2 - paddingLeft
  }

  // Snap vertically if within horizontal guide's snap zone
  if (horizontalSnapZone) {
    const { guidePosition } = horizontalSnapZone
    // Calculate perfect snap position (guide center minus half item height, in CSS coords)
    finalY = guidePosition - item.height / 2 - paddingTop
  }

  return { finalX, finalY }
}
